// services/school-ui/src/pages/chat/attachments/promoteToLibrary.ts
//
// "Save chat attachment to my library." Per-conversation attachments
// (POST /v1/conversations/{cid}/attachments, PR #434) are scoped to
// the thread that uploaded them — the agent can see them within the
// conversation, but the file isn't in the teacher's persistent
// /documents library and isn't discoverable from future chats or
// promotable to the school-wide knowledge base.
//
// This helper closes that gap: fetches the attachment bytes from
// Nova OS with the teacher's bearer, then re-posts them to
// /api/documents/upload/ (the same endpoint NotesFilesPage uses), so
// the file lands in users/<user_id>/ as a real document. From there
// the existing PromoteToOrgButton flow can elevate it to the
// school-wide collection.
//
// Bytes flow through the browser — for typical lesson PDFs / scans
// that's fine; if we ever support large multi-GB attachments we'd
// add a server-side proxy that does the byte-shuffle in one hop.

export class PromoteToLibraryError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = "PromoteToLibraryError";
  }
}

// Auto-save a chat upload into the teacher's persistent /documents library
// (top-level 我的文件, not the artifacts/ subfolder). Unlike
// promoteAttachmentToLibrary this takes the in-memory File directly — the
// composer adapter already holds the bytes, so there's no download round-
// trip. Best-effort: the caller fires this without awaiting so a documents-
// store hiccup never blocks the chat attachment (the agent can still use
// the conversation-scoped copy). Lands under users/<user_id>/ server-side.
export async function saveFileToMyFiles(
  authHeader: Record<string, string>,
  file: File,
): Promise<void> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  const up = await fetch("/api/documents/upload/", {
    method: "POST",
    headers: authHeader, // no Content-Type — FormData sets the boundary
    body: fd,
  });
  if (!up.ok) {
    const body = (await up.json().catch(() => ({}))) as { detail?: string };
    throw new PromoteToLibraryError(
      body.detail ?? `save-to-my-files failed (${up.status})`,
      up.status,
    );
  }
}

interface Args {
  downloadUrl: string;
  filename: string;
  mimeType: string;
  authHeader: Record<string, string>;
}

export async function promoteAttachmentToLibrary({
  downloadUrl,
  filename,
  mimeType,
  authHeader,
}: Args): Promise<void> {
  // Step 1 — pull the bytes from Nova OS with the teacher's bearer.
  // download_url is a Nova-OS-authenticated path (relative or absolute);
  // the link-click flow would 401 without the header.
  const dl = await fetch(downloadUrl, { headers: authHeader });
  if (!dl.ok) {
    throw new PromoteToLibraryError(
      `download failed (${dl.status})`,
      dl.status,
    );
  }
  const blob = await dl.blob();

  // Step 2 — re-upload via /api/documents/upload/artifacts/ so chat-
  // generated files land in the same folder NotesFilesPage's 生成产物
  // panel is wired to (FolderPanel subdir="artifacts"). The
  // teacher's at-a-glance "where do my AI outputs live?" answer is
  // now just /documents/artifacts/, not a mix of home dir + manual
  // sorting. Nova OS still rewrites the path under users/<user_id>/
  // so cross-teacher isolation is unchanged.
  const fd = new FormData();
  fd.append("file", new File([blob], filename, { type: mimeType || blob.type }));

  const up = await fetch("/api/documents/upload/artifacts/", {
    method: "POST",
    headers: authHeader,
    body: fd,
  });
  if (!up.ok) {
    const body = (await up.json().catch(() => ({}))) as { detail?: string };
    throw new PromoteToLibraryError(
      body.detail ?? `upload failed (${up.status})`,
      up.status,
    );
  }
}
